import { Box, styled, useTheme } from '@mui/material';
import React, { CSSProperties, ImgHTMLAttributes } from 'react';
import brandclub_colors from '../../../colors/brandclubColors';
import { CampaignType } from '../../../types';
import { getImageAssetUrl } from '../../../utils/assetUtils';
import { useOpticalFontCenter } from '../../../utils/opticalFontCenter';
import { formatMoney } from '../../../utils/stringUtils';

type RewardPillWithTextProps = {
  rewardAmount: number;
  rewardCampaignType: CampaignType;
  rewardPercent?: number;
  rewardPercentText?: string;
  rewardPercentTextOverride?: string;
  containerStyle?: CSSProperties;
  pillContainerStyle?: CSSProperties;
  onPillClick?: React.MouseEventHandler<HTMLDivElement>;
  baseFontSize?: number;
  className?: string;
};

const StyledContainer = styled('div', {
  shouldForwardProp: (prop) => !['rewardCampaignType', 'clickable'].includes(prop.toString()),
})<{ rewardCampaignType: RewardPillWithTextProps['rewardCampaignType']; clickable: boolean }>(
  ({ clickable, theme }) => ({
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    columnGap: 7,
    '*': {
      mixBlendMode: 'unset !important',
    },
    '.reward-pill': {
      textWrap: 'nowrap',
      borderRadius: 25,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: 0,
      display: 'flex',
      alignItems: 'center',
      ':hover': {
        cursor: clickable ? 'pointer' : 'default',
      },
    },
    '.rewardPercent-text': {
      color: theme.palette.primary.main,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: 0.24,
      whiteSpace: 'nowrap', // added for mobile view
      textOverflow: 'ellipsis',
      overflow: 'hidden',
    },
  }),
);

// default styles are from pdp buy-box
const getConfigByRewardCampaignType = (
  rewardCampaignType: CampaignType,
  customPillColorPurchase?: string,
  customPillColorPurchaseText?: string,
  customPillColorSurvey?: string,
  customPillColorSurveyText?: string,
  customPillColorContent?: string,
  customPillColorContentText?: string,
  customPillColorAffiliate?: string,
  customPillColorAffiliateText?: string,
  customPillColorDailyDeal?: string,
  customPillColorDailyDealText?: string,
  customPillColorDefault?: string,
  customPillColorDefaultText?: string,
): {
  iconImgProps: ImgHTMLAttributes<HTMLImageElement>;
  rewardTypeDisplay: string;
  containerStyle: CSSProperties;
} => {
  switch (rewardCampaignType) {
    case CampaignType.DailyDealsRewardCampaign:
      return {
        iconImgProps: {
          src: getImageAssetUrl('instant-reward-icon.webp'),
          alt: 'instant-reward-icon',
        },
        rewardTypeDisplay: 'instant',
        containerStyle: {
          color: customPillColorDailyDealText ? customPillColorDailyDealText : brandclub_colors.orange_EC792E,
          backgroundColor: customPillColorDailyDeal ? customPillColorDailyDeal : brandclub_colors.orange_EC792E_30,
        },
      };
    case CampaignType.PurchaseRewardCampaign:
      return {
        iconImgProps: {
          src: getImageAssetUrl('purchase-reward-icon.webp'),
          alt: 'purchase-reward-icon',
        },
        rewardTypeDisplay: 'purchase',
        containerStyle: {
          color: customPillColorPurchaseText ? customPillColorPurchaseText : brandclub_colors.magenta_A22272,
          backgroundColor: customPillColorPurchase ? customPillColorPurchase : brandclub_colors.magenta_A22272_30,
        },
      };
    case CampaignType.AffiliateRewardCampaign:
      return {
        iconImgProps: {
          src: getImageAssetUrl('affiliate-reward-icon.webp'),
          alt: 'affiliate-reward-icon',
        },
        rewardTypeDisplay: '',
        containerStyle: {
          color: customPillColorAffiliateText ? customPillColorAffiliateText : brandclub_colors.teal_16949B,
          backgroundColor: customPillColorAffiliate ? customPillColorAffiliate : brandclub_colors.teal_16949B_20,
        },
      };
    case CampaignType.ContentRewardCampaign:
      return {
        iconImgProps: {
          src: getImageAssetUrl('content-reward-icon.webp'),
          alt: 'content-reward-icon',
        },
        rewardTypeDisplay: '',
        containerStyle: {
          color: customPillColorContentText ? customPillColorContentText : brandclub_colors.magenta_A22272,
          backgroundColor: customPillColorContent ? customPillColorContent : brandclub_colors.magenta_A22272_30,
        },
      };
    case CampaignType.SurveyRewardCampaign:
      return {
        iconImgProps: {
          src: getImageAssetUrl('survey-reward-icon.webp'),
          alt: 'survey-reward-icon',
        },
        rewardTypeDisplay: '',
        containerStyle: {
          color: customPillColorSurveyText ? customPillColorSurveyText : brandclub_colors.magenta_A22272,
          backgroundColor: customPillColorSurvey ? customPillColorSurvey : brandclub_colors.magenta_A22272_30,
        },
      };
    default:
      return {
        iconImgProps: {
          src: undefined,
          alt: 'affiliate-reward-icon',
        },
        rewardTypeDisplay: '',
        containerStyle: {
          color: customPillColorDefaultText ? customPillColorDefaultText : brandclub_colors.teal_16949B,
          backgroundColor: customPillColorDefault ? customPillColorDefault : brandclub_colors.teal_16949B_20,
        },
      };
  }
};

export const RewardPillWithText = ({
  rewardAmount,
  rewardCampaignType,
  rewardPercent,
  rewardPercentText,
  rewardPercentTextOverride,
  containerStyle: containerStyleOverride = {},
  pillContainerStyle = {},
  baseFontSize = 14,
  onPillClick,
  className,
}: RewardPillWithTextProps) => {
  const theme = useTheme();
  const centerFontElements = useOpticalFontCenter();

  const customPillColor = theme?.customRewardPillColor;

  const { iconImgProps, rewardTypeDisplay, containerStyle } = getConfigByRewardCampaignType(
    rewardCampaignType,
    customPillColor?.purchase,
    customPillColor?.purchaseText,
    customPillColor?.survey,
    customPillColor?.surveyText,
    customPillColor?.content,
    customPillColor?.contentText,
    customPillColor?.affiliate,
    customPillColor?.affiliateText,
    customPillColor?.dailyDeal,
    customPillColor?.dailyDealText,
    customPillColor?.default,
    customPillColor?.defaultText,
  );

  const { ...restIconImgProps } = iconImgProps;

  const pillDefaultPadding = {
    paddingTop: `${baseFontSize / 3}px`, // top slightly larger than bottom to adjust text alignment
    paddingLeft: `${baseFontSize * 0.95}px`,
    paddingBottom: `${baseFontSize / 3}px`,
    paddingRight: `${baseFontSize * 0.95}px`,
  };

  const subfixText = rewardPercentTextOverride
    ? rewardPercentTextOverride
    : rewardPercent
    ? Math.round(rewardPercent) + '% ' + (rewardPercentText ?? `${rewardTypeDisplay} reward`)
    : null;

  return (
    <StyledContainer
      rewardCampaignType={rewardCampaignType}
      style={{ display: rewardAmount ? 'flex' : 'none', ...containerStyleOverride }}
      clickable={!!onPillClick}
      className={className}
    >
      <Box sx={{ borderRadius: '999px', backgroundColor: '#fff', display: 'flex' }}>
        <Box
          className="reward-pill"
          sx={{
            ...containerStyle,
            ...pillDefaultPadding,
            ...pillContainerStyle,
            fontWeight: 600,

            fontFamily: 'inherit',
          }}
          onClick={onPillClick}
        >
          {iconImgProps.src ? (
            <img
              {...restIconImgProps}
              style={{
                maxWidth: baseFontSize,
                maxHeight: baseFontSize,
                objectFit: 'contain',
                marginRight: baseFontSize * 0.416,
                verticalAlign: 'middle',
              }}
            />
          ) : null}
          <Box
            component="span"
            ref={(ref: HTMLSpanElement | null) => {
              ref && centerFontElements(ref);
            }}
            sx={{
              margin: 0,
              padding: 0,
            }}
          >
            {formatMoney(rewardAmount)}
          </Box>
        </Box>
      </Box>

      {subfixText ? (
        <div
          className="rewardPercent-text"
          ref={(ref) => {
            ref && centerFontElements(ref);
          }}
          style={{
            fontWeight: 600,
            fontFamily: 'inherit',
          }}
        >
          {subfixText}
        </div>
      ) : null}
    </StyledContainer>
  );
};
